import React from 'react'
import { defineMessages, IntlShape } from 'react-intl'

import { Appointment } from '../../models/appointment/Appointment'
import { ConversationObject } from '../../models/conversation/Conversation'
import { CONVERSATION_STATE, CONVERSATION_TYPE, MessageConversation } from '../../models/messages/Messages'

import type { Episode } from '../../models/episodes/Episodes'

const childBadgeMessages = defineMessages({
  childBadge: {
    defaultMessage: 'For {name}',
    description: 'Badge label shown above a messaging thread that belongs to a child/dependent of the logged-in parent',
  },
})

export const getChildBadgeLabel = (
  intl: IntlShape,
  appointments: Appointment[],
  patientLyraId: string,
  currentUserId: string,
): string | undefined => {
  if (!currentUserId || patientLyraId === currentUserId) {
    return undefined
  }
  const match = appointments.find((a) => {
    if (a.childLyraId === patientLyraId) {
      return true
    }
    if (a.guardianLyraId && a.userId === patientLyraId) {
      return true
    }
    return false
  })
  if (!match) {
    return undefined
  }
  const firstName = match.childFirstName ?? match.userInfo?.firstName
  if (!firstName) {
    return undefined
  }
  return intl.formatMessage(childBadgeMessages.childBadge, { name: firstName })
}

type SubtitleParameters = {
  isProviderBlocked: boolean
  countryEmergencyNumbers: string[]
  sessionTime: string
  inVideoSession: boolean
  hasActiveAppointment: boolean
  isGenAI: boolean
  isProviderDA?: boolean
  providerName?: string
  contactCarePhoneNumber?: string
  renderLink: (phone: string) => React.ReactNode
  renderContactLyraLink: () => void
}

export const getChatHeaderSubTitleMsg = ({
  isProviderBlocked,
  countryEmergencyNumbers,
  inVideoSession,
  hasActiveAppointment,
  sessionTime,
  isGenAI,
  providerName,
  isProviderDA,
  contactCarePhoneNumber,
  renderLink,
  renderContactLyraLink,
}: SubtitleParameters) => {
  if (isProviderBlocked) {
    if (isProviderDA && contactCarePhoneNumber && countryEmergencyNumbers) {
      return React.createElement(
        React.Fragment,
        null,
        `is unavailable. For immediate crisis support, contact `,
        renderLink(contactCarePhoneNumber),
        `, ext 2, or call `,
        renderLink(countryEmergencyNumbers[0]),
        `.`,
      )
    }
    return `is unavailable. Call ${countryEmergencyNumbers} for emergencies.`
  }
  if (inVideoSession) {
    return ''
  }
  if (isGenAI) {
    return React.createElement(
      React.Fragment,
      null,
      `Lyra AI is not human and can make mistakes. Do not use it for crisis support. For urgent help, `,
      renderContactLyraLink(),
      ` or `,
      renderLink(countryEmergencyNumbers[0]),
      `.`,
    )
  }
  if (hasActiveAppointment) {
    return `Session today from ${sessionTime}`
  }
  if (isProviderDA) {
    if (providerName && contactCarePhoneNumber && countryEmergencyNumbers) {
      // Build a React fragment with clickable tel: links using the provided renderer
      return React.createElement(
        React.Fragment,
        null,
        `${providerName} will respond as soon as they can. For immediate crisis support, contact `,
        renderLink(contactCarePhoneNumber),
        `, ext 2, or call `,
        renderLink(countryEmergencyNumbers[0]),
        `.`,
      )
    }
    // Fallback to plain string if a renderer isn't provided
    return `${
      providerName ?? 'Your provider'
    } will respond as soon as they can. For immediate crisis support, contact ${contactCarePhoneNumber}, ext 2, or call ${countryEmergencyNumbers}.`
  }
  return `Usually responds within 2 days. Call ${countryEmergencyNumbers} for emergencies.`
}

export const isActiveAiConversation = (conversation: MessageConversation) => {
  return (
    conversation.conversation_attributes?.type === CONVERSATION_TYPE.AI_COACH &&
    conversation.conversation_state !== CONVERSATION_STATE.CLOSED &&
    conversation.conversation_state !== CONVERSATION_STATE.PENDING
  )
}

export const getAiCoachPromoState = ({
  conversations,
  genAiOptOut,
  isAiCoachEnabled,
  episodes,
}: {
  conversations: MessageConversation[]
  genAiOptOut: boolean
  isAiCoachEnabled: boolean
  episodes: Episode[]
}): { showAiCoach: boolean; aiCoachPromoEnabledProviderIds: string[] } => {
  const hasAICoachConversations = conversations.length > 0
  const showAiCoach = Boolean(hasAICoachConversations && !genAiOptOut && isAiCoachEnabled)
  const activeEpisodeIds = conversations
    .filter((c) => c.provider_participant_status === 'active')
    .map((c) => c.conversation_attributes.episode_id)
    .filter((id) => id !== undefined)
  const aiCoachPromoEnabledProviderIds = episodes
    .filter((e) => activeEpisodeIds.includes(e.id))
    .map((e) => e.provider_id)

  return {
    showAiCoach,
    aiCoachPromoEnabledProviderIds: showAiCoach ? aiCoachPromoEnabledProviderIds : [],
  }
}

export const getClientHasActiveAiConversation = (
  clientId: string,
  conversations?: MessageConversation[] | ConversationObject[],
) => {
  const clientAiCoachConversations = conversations?.filter(
    (conversation) =>
      conversation.patient_lyra_id === clientId &&
      conversation.conversation_attributes?.type === CONVERSATION_TYPE.AI_COACH,
  )
  const hasActiveAiCoachConversation = clientAiCoachConversations?.some(
    (conversation) => conversation.conversation_state === CONVERSATION_STATE.ACTIVE,
  )
  return Boolean(hasActiveAiCoachConversation)
}
